import React, { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loading } from "../../components/loading";
import { AccountChoice } from "../../components/accountChoice";
import { LinkedAccount, useAuth } from "../../hooks/auth";
import CpfCnpj from "@react-br-forms/cpf-cnpj-mask";
import { ExclamationIcon } from "@heroicons/react/solid";

import {
  ButtonEyeVisibility,
  ButtonSignIn,
  Container,
  Content,
  EyeDisabledIcon,
  EyeEnabledIcon,
  ForgetPassword,
  Input,
  InputPassword,
  Line,
  NotHaveAnAccount,
  Reference,
  SecondaryLoginLink,
  Separator,
  Title,
  WrapperBackground,
  WrapperContent,
  WrapperForm,
  WrapperLogo,
  Error,
} from "../signIn/styles";
import { parseError } from "../../services/api";
import { useClient } from "../../hooks/client";

export function SignInMulti() {
  const { client } = useClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigation = useNavigate();
  const { signInMulti, chooseAccount, account } = useAuth();

  const [document, setDocument] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [choices, setChoices] = useState<LinkedAccount[]>([]);

  const handlePasswordVisibility = () => {
    setIsPasswordVisible((state) => !state);
  };

  useEffect(() => {
    if (account) {
      navigation("/");
    }
  });

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault();

    if (!document || !email || !password) {
      return;
    }

    try {
      setLoading(true);
      setError("");
      const linked = await signInMulti({
        document: document.replace(/[/,.,-\s]/g, ""),
        email,
        password,
      });

      setLoading(false);
      if (linked) {
        setChoices(linked);
        return;
      }
      navigation("/");
    } catch (err) {
      const parsed = parseError(err);
      setLoading(false);
      setError(parsed.friend);
    }
  };

  const handleChoose = async (accountId: string) => {
    try {
      setLoading(true);
      setError("");
      await chooseAccount(accountId);
      setLoading(false);
      navigation("/");
    } catch (err) {
      setLoading(false);
      setError(parseError(err).friend);
    }
  };

  return (
    <Container>
      <WrapperBackground />
      <WrapperContent>
        <Content>
          <WrapperLogo>
            {!client.hideLogo && (
            <img
              src={client.whiteLogo.image}
              alt={client.name}
              style={{
                width: client.whiteLogo.maxWidth,
                height: client.whiteLogo.maxHeight,
              }}
            />
            )}
          </WrapperLogo>
          <Title>
            Acesso com identificação <br />
            <b>completa</b>
          </Title>
          <Separator />
          <Reference>
            {choices.length > 0
              ? "Você tem mais de uma conta. Escolha qual deseja acessar:"
              : "Informe CPF/CNPJ, e-mail cadastrado e senha para entrar"}
          </Reference>
          {choices.length > 0 ? (
            <>
              <AccountChoice
                accounts={choices}
                loading={loading}
                onChoose={handleChoose}
                onBack={() => setChoices([])}
              />
              {!!error && <p className="mt-4 text-sm text-red-700">{error}</p>}
            </>
          ) : (
          <WrapperForm onSubmit={handleSignIn}>
            <Input>
              <span>CPF/CNPJ:</span>
              <CpfCnpj
                placeholder="Digite seu CPF/CNPJ"
                type="text"
                onChange={(input: React.ChangeEvent<HTMLInputElement>) =>
                  setDocument(input.target.value)
                }
                value={document}
              />
            </Input>
            <Input>
              <span>E-mail:</span>
              <input
                type="email"
                autoComplete="email"
                onChange={(e) => setEmail(e.target.value)}
                value={email}
                placeholder="Digite seu e-mail"
              />
            </Input>
            <InputPassword>
              <div>
                <span>Senha:</span>
                <input
                  onChange={(input) => setPassword(input.target.value)}
                  value={password}
                  placeholder="Digite sua senha"
                  type={isPasswordVisible ? "text" : "password"}
                />
              </div>
              <ButtonEyeVisibility
                onClick={handlePasswordVisibility}
                type="button"
              >
                {isPasswordVisible ? <EyeEnabledIcon /> : <EyeDisabledIcon />}
              </ButtonEyeVisibility>
            </InputPassword>
            {!!error && (
              <Error>
                <div className="bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <ExclamationIcon
                        className="h-5 w-5 text-red-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              </Error>
            )}
            <ButtonSignIn type="submit">
              <Loading isLoading={loading} />
              Entrar
            </ButtonSignIn>
          </WrapperForm>
          )}
          <SecondaryLoginLink to="/signin">
            Voltar ao login principal
          </SecondaryLoginLink>
          <ForgetPassword href="/forgot">Esqueci minha senha</ForgetPassword>
          <Line />
          <NotHaveAnAccount to="/type-create-account">
            Não tem uma conta? Abrir conta {client.name}
          </NotHaveAnAccount>
        </Content>
      </WrapperContent>
    </Container>
  );
}
